import { Injectable } from '@angular/core';
import { Device } from '@capacitor/device';
import { PushNotifications, Token } from '@capacitor/push-notifications';
import { AuthenticatedHttpService } from '../authenticatedHttpService.service';
import { CommonPushNotification, NotificationType } from 'src/shared/models/push-notification/push-notification';
import { LocalStorageService } from '../local-storage.service';
import { environment } from 'src/environments/environment';
import { Subject } from 'rxjs';
import { HttpUtils } from 'src/shared/utils/http-utils';
import { PUSH_NOTIFICATION_CHANNELS } from './push-notification.channels';
import { StorageProvider } from '../storage.provider';
import { App } from '@capacitor/app';

export interface IUnreadPushNotification {
    type: NotificationType,
    count: number,
}

@Injectable({ providedIn: "root" })
export class PushProvider {
    static readonly PUSH_NOTIFICATIONS = "PushProvider_PushNotifications";

    static readonly FIREBASE_ID: string = "FIREBASE_ID";
    static readonly LINK_REGISTER: string = "deviceregistrations";
    static readonly LINK_UNREGISTER: string = "deviceregistrations/firebasedeviceid/";

    private readonly _onPushNotification = new Subject<CommonPushNotification>();
    public onPushNotification$ = this._onPushNotification.asObservable();

    deviceRegistered: boolean;
    deviceRegistration: boolean;
    denyPermission: boolean;

    constructor(
        private http: AuthenticatedHttpService,
        private storage: LocalStorageService,
        private storageProvider: StorageProvider,
    ) { }

    async setupPush() {
        try {
            await PushNotifications.requestPermissions().then(async permission => {
                console.log('push permission: ', permission.receive)
                if (permission.receive === 'granted') {
                    console.log('granted push...')
                    await PushNotifications.register();
                }
                else {
                    this.denyPermission = true;
                }
            });

            await PushNotifications.addListener('registration', async (token: Token) => {
                console.log('registration push...')
                await this.registerDevice(token.value);
            });

            await PushNotifications.addListener('registrationError', err => {
                console.error('Registration error: ', err.error);
            });
        }
        catch (e) {
            console.log(e)
        }
    }

    async purge() {
        if (!this.deviceRegistered) return;

        this.deviceRegistered = false;
        const firebaseId = await this.storage.get(PushProvider.FIREBASE_ID);
        try {
            await this.http.delete(environment.web.apiUrl + PushProvider.LINK_UNREGISTER + encodeURIComponent(firebaseId))
        } catch (error) {
            console.log("Device error while Unregistering on server", error);
        }

        await PushNotifications.removeAllListeners();
        await PushNotifications.unregister();
    }

    listen() {
        // receiving topic push-notification event
        if (this.storageProvider.isAndroid) {
            this.createNotificationChannels();
        }

        // receiving push-notification event
        this.onReceivedMessage();

        // click on push-notification event
        this.onReceivedMessageClick();
    }

    private async registerDevice(token: string) {
        try {
            if (this.deviceRegistration || this.deviceRegistered) return;
            this.deviceRegistration = true;

            var device = await Device.getInfo();
            var deviceId = await Device.getId();
            const appInfo = await App.getInfo();

            const response = await HttpUtils.handle<string>({
                request: this.http.post(environment.web.apiUrl + PushProvider.LINK_REGISTER, JSON.stringify({
                    name: device.model,
                    description: deviceId.identifier,
                    firebaseDeviceId: token,
                    appVersion: appInfo.version,
                })),
                stringifyError: true,
            });

            await this.storage.set(PushProvider.FIREBASE_ID, token);

            this.deviceRegistered = true;
        }
        catch (error) {
            if (error === 'OK') {
                await this.storage.set(PushProvider.FIREBASE_ID, token);
                console.log('device already registered');
                this.deviceRegistered = true;
            }
            else {
                console.log("Device error while registering on server", error);
                this.deviceRegistered = false;
            }
        }
        finally {
            this.deviceRegistration = false;
        }
    }

    private onReceivedMessage() {
        PushNotifications.addListener('pushNotificationReceived', pushNotification => {
            if (!pushNotification.data) return;

            const notification = {
                title: pushNotification.title,
                description: pushNotification.body,
                clickAction: parseInt(pushNotification.data.click_action.toString()),
                associatedPage: parseInt(pushNotification.data.associated_page.toString()),
                associatedEntryId: pushNotification.data.associated_entry_id,
                additional: pushNotification.data.additional,
                clickNotification: false,
                createdBy: pushNotification.data.created_by,
                createdAt: pushNotification.data.created_at,
                databaseIdentifier: pushNotification.data.dbi,
                type: parseInt(pushNotification.data.type.toString()),
            } as CommonPushNotification;

            this._onPushNotification.next(notification);
        });
    }

    private async createNotificationChannels() {
        PUSH_NOTIFICATION_CHANNELS.forEach(async channel => {
            await PushNotifications.createChannel({
                id: channel.id,
                name: channel.name,
                importance: channel.importance,
            });
        });
    }

    private onReceivedMessageClick() {
        PushNotifications.addListener('pushNotificationActionPerformed', pushNotification => {
            if (!pushNotification.notification.data) return;

            const notification = {
                title: pushNotification.notification.title,
                description: pushNotification.notification.body,
                clickAction: parseInt(pushNotification.notification.data.click_action.toString()),
                associatedPage: parseInt(pushNotification.notification.data.associated_page.toString()),
                associatedEntryId: pushNotification.notification.data.associated_entry_id,
                additional: pushNotification.notification.data.additional,
                clickNotification: true,
                createdBy: pushNotification.notification.data.created_by,
                createdAt: pushNotification.notification.data.created_at,
                databaseIdentifier: pushNotification.notification.data.dbi,
                type: parseInt(pushNotification.notification.data.type.toString()),
            } as CommonPushNotification;

            this._onPushNotification.next(notification);
        });
    }
}